import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon, SlidersHorizontalIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}
export interface FilterSection {
  id: string;
  title: string;
  type: 'checkbox' | 'radio';
  options: FilterOption[];
  value: string[] | string;
  onChange: (value: any) => void;
}
export interface SubNavItem {
  label: string;
  href: string;
  isActive?: boolean;
}
export interface NavItem {
  label: string;
  href: string;
  icon?: React.ElementType;
  isActive?: boolean;
  subItems?: SubNavItem[];
}
interface FilterSidebarProps {
  title?: string;
  navigation?: NavItem[];
  sections: FilterSection[];
  onReset: () => void;
  searchable?: boolean;
  className?: string;
}
export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  title = 'Filters',
  navigation = [],
  sections,
  onReset,
  searchable = true,
  className = ''
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const location = useLocation();
  const [filterSearch, setFilterSearch] = useState('');
  const [expandedNavItem, setExpandedNavItem] = useState<string | null>(null);
  // Auto-expand nav item if a sub-item is active OR if the parent item is active
  useEffect(() => {
    const activeParent = navigation.find(item => item.isActive && item.subItems && item.subItems.length > 0 || item.subItems?.some(sub => sub.isActive || location.pathname + location.search === sub.href));
    if (activeParent) {
      setExpandedNavItem(activeParent.label);
    }
  }, [navigation, location]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    sections.forEach(s => initial[s.id] = true);
    return initial;
  });
  // Toggle section collapse
  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  // Filter options based on search
  const filteredSections = useMemo(() => {
    if (!filterSearch.trim()) return sections;
    return sections.map(section => {
      // If section title matches, keep all options
      if (section.title.toLowerCase().includes(filterSearch.toLowerCase())) {
        return section;
      }
      // Otherwise filter options
      const filteredOptions = section.options.filter(opt => opt.label.toLowerCase().includes(filterSearch.toLowerCase()));
      if (filteredOptions.length === 0) return null;
      return {
        ...section,
        options: filteredOptions
      };
    }).filter(Boolean) as FilterSection[];
  }, [sections, filterSearch]);
  return <div className={`flex flex-col h-full ${className}`} data-id="element-3755" data-mp="2ppw4h5pdiseo">
      {/* Header / Search */}
      <div className="space-y-4 mb-2" data-id="element-3756" data-mp="2kpgt2jonjuzc">
        {searchable && <div className="relative" data-id="element-3757" data-mp="2go53uf9oxbro">
            <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-3758" data-mp="2gp6cye75bo6r" />
            <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Find a filter..." className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-3759" data-mp="2bqwhs3lf8nl5" />
            {filterSearch && <button onClick={() => setFilterSearch('')} className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full ${dk.dropdownItemHover} ${dk.textMuted}`} data-id="element-3760" data-mp="2un4rbjlgvkm9">
                <XIcon className="h-3 w-3" data-id="element-3761" data-mp="3nbabo58lz7lk" />
              </button>}
          </div>}
      </div>

      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-6" data-id="element-3762" data-mp="36zf9twasb8gn">
        {/* Navigation Section */}
        {navigation.length > 0 && <div className={`pb-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-3763" data-mp="2xxfq2w8dxqa0">
            <nav className="space-y-1" data-id="element-3764" data-mp="32w0qowrjubyz">
              {navigation.map(item => {
            const Icon = item.icon;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedNavItem === item.label;
            const isActive = item.isActive ?? location.pathname === item.href;
            return <div key={item.label} data-id="element-3765" data-mp="3lss7mzjmcezv">
                    {hasSubItems ? <button onClick={() => setExpandedNavItem(isExpanded ? null : item.label)} className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive || isExpanded ? dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700' : dk.isDark ? 'text-gray-300 hover:bg-gray-800 hover:text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-3766" data-mp="ddi921extrwu">
                        <div className="flex items-center" data-id="element-3767" data-mp="8kxvmmukv602">
                          {Icon && <Icon className={`mr-3 h-4 w-4 flex-shrink-0 ${isActive || isExpanded ? dk.isDark ? 'text-indigo-400' : 'text-indigo-500' : dk.textMuted}`} data-id="element-3768" data-mp="4jvtny9a0fue" />}
                          {item.label}
                        </div>
                        {isExpanded ? <ChevronDownIcon className="h-4 w-4 opacity-50" data-id="element-3769" data-mp="1twpn2qq5ofqz" /> : <ChevronRightIcon className="h-4 w-4 opacity-50" data-id="element-3770" data-mp="1p1jytl9a6ozq" />}
                      </button> : <Link to={item.href} className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700' : dk.isDark ? 'text-gray-300 hover:bg-gray-800 hover:text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-3771" data-mp="210pe6lzu3vk4">
                        {Icon && <Icon className={`mr-3 h-4 w-4 flex-shrink-0 ${isActive ? dk.isDark ? 'text-indigo-400' : 'text-indigo-500' : dk.textMuted}`} data-id="element-3772" data-mp="1nac68jmy0gus" />}
                        {item.label}
                      </Link>}

                    {/* Sub-items */}
                    {hasSubItems && isExpanded && <div className="mt-1 space-y-1" data-id="element-3773" data-mp="s1s2prrldm0k">
                        {item.subItems?.map(subItem => {
                  const isSubActive = subItem.isActive ?? location.pathname + location.search === subItem.href;
                  return <Link key={subItem.label} to={subItem.href} className={`flex items-center pl-10 pr-3 py-1.5 text-sm font-medium rounded-md transition-colors ${isSubActive ? dk.isDark ? 'text-indigo-300 bg-indigo-900/20' : 'text-indigo-700 bg-indigo-50' : dk.isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-3774" data-mp="d36e3rzm5w12">
                              {subItem.label}
                            </Link>;
                })}
                      </div>}
                  </div>;
          })}
            </nav>
          </div>}

        {/* Filter Sections */}
        <div className="space-y-1" data-id="element-3775" data-mp="1e7t5sy9x105d">
          {filteredSections.map(section => <div key={section.id} className={`border-b py-4 last:border-b-0 ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-3776" data-mp="zasciqc2sx4s">
              <button onClick={() => toggleSection(section.id)} className="flex w-full items-center justify-between text-left group" data-id="element-3777" data-mp="qiqcuu9tzkvk">
                <h3 className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-3778" data-mp="glcbm1whb2nu">
                  {section.title}
                </h3>
                {expandedSections[section.id] ? <ChevronUpIcon className={`h-4 w-4 ${dk.textMuted} group-hover:opacity-80`} data-id="element-3779" data-mp="7k4dbjtyq1tr" /> : <ChevronDownIcon className={`h-4 w-4 ${dk.textMuted} group-hover:opacity-80`} data-id="element-3780" data-mp="bl7t66c0fxuz" />}
              </button>

              {expandedSections[section.id] && <div className="mt-3 space-y-2" data-id="element-3781" data-mp="3k5t7m5x9cwn4">
                  {section.options.map(option => {
              const isSelected = section.type === 'checkbox' ? (section.value as string[]).includes(option.value) : section.value === option.value;
              return <label key={option.value} className="flex items-start cursor-pointer group" data-id="element-3782" data-mp="6w5a4vephkao">
                        <div className="flex items-center h-5" data-id="element-3783" data-mp="gmhv2oqfnfhc">
                          <input type={section.type} name={section.id} value={option.value} checked={isSelected} onChange={() => {
                    const scrollY = window.scrollY;
                    section.onChange(option.value);
                    requestAnimationFrame(() => {
                      window.scrollTo({
                        top: scrollY,
                        behavior: 'instant' as ScrollBehavior
                      });
                    });
                  }} className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${dk.isDark ? 'bg-gray-700 border-gray-600 checked:bg-indigo-600' : ''}`} data-id="element-3784" data-mp="gp4bzez7r7c8" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-3785" data-mp="1x92cfs8kmd1y">
                          <span className={`font-medium ${isSelected ? dk.textPrimary : `${dk.textSecondary} group-hover:${dk.isDark ? 'text-gray-300' : 'text-gray-800'}`}`} data-id="element-3786" data-mp="qtrwcfl6mhxl">
                            {option.label}
                          </span>
                          {option.count !== undefined && <span className={`ml-1.5 text-xs ${dk.textMuted}`} data-id="element-3787" data-mp="gq4i68rt1052">
                              ({option.count})
                            </span>}
                        </div>
                      </label>;
            })}
                </div>}
            </div>)}

          {filteredSections.length === 0 && <div className={`text-sm py-4 text-center ${dk.textMuted}`} data-id="element-3788" data-mp="zehy15wrfme0">
              No filters match "{filterSearch}"
            </div>}
        </div>
      </div>

      {/* Reset Link */}
      <div className={`pt-4 mt-2 border-t ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-3789" data-mp="19dqv47mx9h4l">
        <button onClick={onReset} className={`text-sm font-medium hover:underline w-full text-left ${rt.accentLink}`} data-id="element-3790" data-mp="369584z4crovh">
          Reset all filters
        </button>
      </div>
    </div>;
};
// Wrapper component handles mobile drawer and desktop sidebar layout
export const FilterSidebarWrapper: React.FC<{
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}> = ({
  children,
  isOpen,
  onClose,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const dk = useDarkClasses();
  return <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-3791" data-mp="3h5qki2ea7cbc" />}

      {/* Mobile Drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'} ${dk.isDark ? 'bg-gray-900 border-r border-gray-800' : 'bg-white border-r border-gray-200'}`} data-id="element-3792" data-mp="3qw33vrad0zfw">
        <div className={`flex items-center justify-between p-4 border-b ${dk.isDark ? 'border-gray-800' : 'border-gray-200'}`} data-id="element-3793" data-mp="nd5j2zgdqqbj">
          <h2 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-3794" data-mp="eb63mbl0juap">
            Filters & Menu
          </h2>
          <button onClick={onClose} className={`p-2 rounded-md transition-colors ${dk.dropdownItemHover} ${dk.textMuted}`} data-id="element-3795" data-mp="ja0mfdizdiut">
            <XIcon className="h-5 w-5" data-id="element-3796" data-mp="4k1q7gazkhhz" />
          </button>
        </div>
        <div className="p-4 h-[calc(100%-65px)] overflow-hidden" data-id="element-3797" data-mp="9d4i7k047iw2">
          {children}
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className={`hidden lg:block flex-shrink-0 transition-all duration-300 ease-in-out ${isCollapsed ? 'w-16' : 'w-72'}`} data-id="element-3798" data-mp="3ro959tvpav9s">
        <div className={`sticky top-24 h-[calc(100vh-8rem)] rounded-xl border shadow-sm transition-all ${dk.card}`} data-id="element-3799" data-mp="3hsxqjbkalo21">
          <div className="flex flex-col h-full" data-id="element-3800" data-mp="3mq7rnoh1no5p">
            {/* Collapse Toggle Header */}
            <div className={`flex items-center justify-between p-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-3801" data-mp="sy7zcyfqqtkf">
              {!isCollapsed && <h2 className={`font-bold text-lg ${dk.textHeading}`} data-id="element-3802" data-mp="k54moscx6kqu">
                  Filters
                </h2>}
              <button onClick={onToggleCollapse} className={`p-1.5 rounded-md transition-colors ${isCollapsed ? 'mx-auto' : ''} ${dk.dropdownItemHover} ${dk.textMuted}`} aria-label={isCollapsed ? 'Expand filters' : 'Collapse filters'} data-id="element-3803" data-mp="a8i5cdjsybtk">
                {isCollapsed ? <SlidersHorizontalIcon className="h-5 w-5" data-id="element-3804" data-mp="3sewfe1eya1mo" /> : <ChevronLeftIcon className="h-5 w-5" data-id="element-3805" data-mp="1vme6jfzyneso" />}
              </button>
            </div>

            {/* Content */}
            {!isCollapsed ? <div className="flex-1 overflow-hidden p-4" data-id="element-3806" data-mp="1y6ak3yzmaw30">{children}</div> : <div className="flex flex-col items-center py-6 space-y-4" data-id="element-3807" data-mp="1y4g8xce5eic4">
                <div className={`w-8 h-1 rounded-full ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-3808" data-mp="233ss9n5t979y" />
                <div className={`w-8 h-1 rounded-full ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-3809" data-mp="2cv6z6g03d9d4" />
                <div className={`w-8 h-1 rounded-full ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-3810" data-mp="1f8gdtspviyob" />
              </div>}
          </div>
        </div>
      </aside>
    </>;
};